import { getSocket, getSocketClientId } from './socket'

const noop = () => { }

const normalizeSessionId = (value) => String(value || '').trim().toUpperCase()

const CUSTOMER_NAME_BLOCKLIST = new Set([
    '',
    'гость',
    'заказчик',
    'guest',
    'customer',
    'client',
])

const normalizeCustomerName = (value) => String(value || '')
    .replace(/[<>]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 80)

const getCustomerNameStorageKey = (officeId, sessionId) => (
    `office_sales_session_customer_name:${String(officeId || '')}:${normalizeSessionId(sessionId)}`
)

const ensureCustomerName = ({ officeId, sessionId, user = {}, role }) => {
    const cleanRole = String(role || user?.role || '').trim().toLowerCase()

    if (!['customer', 'client', 'guest', 'buyer', 'user'].includes(cleanRole)) {
        return { ...user }
    }

    const directName = normalizeCustomerName(user?.name || user?.fullName)

    if (
        directName.length < 2 ||
        CUSTOMER_NAME_BLOCKLIST.has(directName.toLowerCase())
    ) {
        return null
    }

    try {
        sessionStorage.setItem(
            getCustomerNameStorageKey(officeId, sessionId),
            directName
        )
    } catch (_error) { }

    return {
        ...user,
        name: directName,
        fullName: directName,
        role: 'customer',
    }
}

export const createOfficeSalesSessionId = () => {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
        return crypto.randomUUID().replace(/-/g, '').slice(0, 10).toUpperCase()
    }

    return Math.random().toString(36).slice(2, 12).toUpperCase()
}

export const getManagerSalesSessionLink = ({
    officeId,
    sessionId,
    tab = 'products',
}) => {
    if (typeof window === 'undefined') return ''

    const url = new URL(window.location.href)

    url.pathname = '/adminLayout'
    url.searchParams.set('tab', tab)
    url.searchParams.set('office', officeId)
    url.searchParams.set('salesSession', normalizeSessionId(sessionId))
    url.searchParams.delete('customerCatalogSession')
    url.searchParams.delete('customerCatalogRole')

    return url.toString()
}

export const getCustomerSalesSessionLink = ({
    officeId,
    sessionId,
    token = '',
}) => {
    if (typeof window === 'undefined') return ''

    const url = new URL(window.location.origin)

    url.pathname = `/customer-session/${officeId}/${normalizeSessionId(sessionId)}`

    if (token) {
        url.searchParams.set('token', token)
    }

    return url.toString()
}

export const getOfficeSalesSessionSocketId = () => {
    const socket = getSocket()

    return socket?.id || ''
}

export const onOfficeSalesSessionSocketConnect = (handler) => {
    const socket = getSocket()

    socket.on('connect', handler)

    return () => socket.off('connect', handler)
}

export const joinOfficeSalesSession = ({
    officeId,
    sessionId,
    user,
    role,
    inviteToken,
    invitePin = '',
    allowCreate = false,
}, ack = noop) => {
    const socket = getSocket()
    const effectiveRole = role || (inviteToken ? 'customer' : user?.role || '')
    const safeUser = ensureCustomerName({
        officeId,
        sessionId,
        user,
        role: effectiveRole,
    })

    if (!safeUser) {
        queueMicrotask(() => ack({
            ok: false,
            code: 'CUSTOMER_NAME_REQUIRED',
            error: 'Укажите своё имя перед входом в сессию',
        }))
        return socket
    }

    socket.emit('officeSalesSession:join', {
        officeId,
        sessionId: normalizeSessionId(sessionId),
        clientId: getSocketClientId(),
        user: safeUser,
        role: effectiveRole,
        inviteToken,
        invitePin: String(invitePin || '').replace(/\D/g, '').slice(0, 12),
        allowCreate,
        deviceName: typeof navigator !== 'undefined'
            ? `${navigator.platform || 'Устройство'} · ${Number(navigator.maxTouchPoints || 0) > 0 ? 'сенсорное' : 'браузер'}`
            : '',
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent || '' : '',
    }, ack)

    return socket
}


export const leaveOfficeSalesSession = ({
    officeId,
    sessionId,
}, ack = noop) => {
    const socket = getSocket()

    socket.emit('officeSalesSession:leave', {
        officeId,
        sessionId: normalizeSessionId(sessionId),
    }, ack)
}

export const kickOfficeSalesSessionUser = ({
    officeId,
    sessionId,
    targetSocketId,
    targetClientId,
    user,
    role,
}, ack = noop) => {
    const socket = getSocket()

    socket.emit('officeSalesSession:kickUser', {
        officeId,
        sessionId: normalizeSessionId(sessionId),
        targetSocketId,
        targetClientId,
        user,
        role,
    }, ack)
}

export const endOfficeSalesSession = ({
    officeId,
    sessionId,
    user,
    role,
}, ack = noop) => {
    const socket = getSocket()

    socket.emit('officeSalesSession:end', {
        officeId,
        sessionId: normalizeSessionId(sessionId),
        user,
        role,
    }, ack)
}

export const emitOfficeSalesSessionUpdate = ({
    officeId,
    sessionId,
    patch,
    user,
    role,
    inviteToken,
    saveSource,
    saveTraceId,
}, ack = noop) => {
    const socket = getSocket()

    socket.emit('officeSalesSession:update', {
        officeId,
        sessionId: normalizeSessionId(sessionId),
        patch,
        user,
        role,
        inviteToken,
        saveSource,
        saveTraceId,
    }, ack)
}

export const emitOfficeSalesSessionCursor = ({
    officeId,
    sessionId,
    cursor,
    user,
    role,
}) => {
    const socket = getSocket()
    const payload = {
        officeId,
        sessionId: normalizeSessionId(sessionId),
        cursor,
        user,
        role,
    }

    if (socket?.volatile && typeof socket.volatile.emit === 'function') {
        socket.volatile.emit('officeSalesSession:cursor', payload)
        return
    }

    socket.emit('officeSalesSession:cursor', payload)
}

export const emitOfficeSalesSessionAction = ({
    officeId,
    sessionId,
    action,
    user,
    role,
    inviteToken,
}, ack = noop) => {
    const socket = getSocket()

    const safeMeta = action?.meta && typeof action.meta === 'object' && !Array.isArray(action.meta)
        ? action.meta
        : {}

    socket.emit('officeSalesSession:action', {
        officeId,
        sessionId: normalizeSessionId(sessionId),
        action: {
            id: action?.id || `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
            type: action?.type || 'click',
            label: action?.label || 'Действие',
            x: Number.isFinite(Number(action?.x)) ? Number(action.x) : null,
            y: Number.isFinite(Number(action?.y)) ? Number(action.y) : null,
            target: action?.target || '',
            source: action?.source || 'office',
            meta: safeMeta,
            pageKey: action?.pageKey || safeMeta.pageKey || '',
            salesViewKey: action?.salesViewKey || safeMeta.salesViewKey || '',
            userId: action?.userId || user?._id || user?.id || '',
            userName: action?.userName || user?.name || user?.fullName || user?.email || '',
            role: action?.role || role || user?.role || '',
            ts: action?.ts || Date.now(),
        },
        user,
        role,
        inviteToken,
    }, ack)
}

export const askOfficeSalesSessionUsers = ({
    officeId,
    sessionId,
}, ack = noop) => {
    const socket = getSocket()

    socket.emit('officeSalesSession:who', {
        officeId,
        sessionId: normalizeSessionId(sessionId),
    }, ack)
}

export const onOfficeSalesSessionJoined = (handler) => {
    const socket = getSocket()
    socket.on('officeSalesSession:joined', handler)
    return () => socket.off('officeSalesSession:joined', handler)
}

export const askOfficeSalesSessionJoinRequests = ({
    officeId,
    sessionId,
    user,
    role,
}, ack = noop) => {
    const socket = getSocket()

    socket.emit('officeSalesSession:joinRequests:who', {
        officeId,
        sessionId: normalizeSessionId(sessionId),
        clientId: getSocketClientId(),
        user,
        role,
    }, ack)
}

export const approveOfficeSalesSessionJoinRequest = ({
    officeId,
    sessionId,
    requestId,
    preset = 'view',
    permissions,
    durationMinutes = 0,
    user,
    role,
}, ack = noop) => {
    const socket = getSocket()

    socket.emit('officeSalesSession:joinRequest:approve', {
        officeId,
        sessionId: normalizeSessionId(sessionId),
        requestId,
        preset,
        permissions,
        durationMinutes,
        clientId: getSocketClientId(),
        user,
        role,
    }, ack)
}

export const rejectOfficeSalesSessionJoinRequest = ({
    officeId,
    sessionId,
    requestId,
    message = '',
    user,
    role,
}, ack = noop) => {
    const socket = getSocket()

    socket.emit('officeSalesSession:joinRequest:reject', {
        officeId,
        sessionId: normalizeSessionId(sessionId),
        requestId,
        message,
        clientId: getSocketClientId(),
        user,
        role,
    }, ack)
}

export const updateOfficeSalesSessionParticipantPermissions = ({
    officeId,
    sessionId,
    targetClientId,
    targetSocketId,
    targetName,
    preset = 'view',
    permissions,
    durationMinutes = 0,
    user,
    role,
}, ack = noop) => {
    const socket = getSocket()

    socket.emit('officeSalesSession:participantPermissions:update', {
        officeId,
        sessionId: normalizeSessionId(sessionId),
        targetClientId,
        targetSocketId,
        targetName,
        preset,
        permissions,
        durationMinutes,
        clientId: getSocketClientId(),
        user,
        role,
    }, ack)
}

export const onOfficeSalesSessionJoinRequest = (handler) => {
    const socket = getSocket()
    socket.on('officeSalesSession:joinRequest', handler)
    return () => socket.off('officeSalesSession:joinRequest', handler)
}

export const onOfficeSalesSessionJoinRequests = (handler) => {
    const socket = getSocket()
    socket.on('officeSalesSession:joinRequests', handler)
    return () => socket.off('officeSalesSession:joinRequests', handler)
}

export const onOfficeSalesSessionJoinPending = (handler) => {
    const socket = getSocket()
    socket.on('officeSalesSession:joinPending', handler)
    return () => socket.off('officeSalesSession:joinPending', handler)
}

export const onOfficeSalesSessionJoinApproved = (handler) => {
    const socket = getSocket()
    socket.on('officeSalesSession:joinApproved', handler)
    return () => socket.off('officeSalesSession:joinApproved', handler)
}

export const onOfficeSalesSessionJoinRejected = (handler) => {
    const socket = getSocket()
    socket.on('officeSalesSession:joinRejected', handler)
    return () => socket.off('officeSalesSession:joinRejected', handler)
}

export const onOfficeSalesSessionPermissionsUpdated = (handler) => {
    const socket = getSocket()
    socket.on('officeSalesSession:permissionsUpdated', handler)
    return () => socket.off('officeSalesSession:permissionsUpdated', handler)
}

export const onOfficeSalesSessionParticipantPermissionsUpdated = (handler) => {
    const socket = getSocket()
    socket.on('officeSalesSession:participantPermissionsUpdated', handler)
    return () => socket.off('officeSalesSession:participantPermissionsUpdated', handler)
}

export const onOfficeSalesSessionSnapshot = (handler) => {
    const socket = getSocket()
    socket.on('officeSalesSession:snapshot', handler)
    return () => socket.off('officeSalesSession:snapshot', handler)
}

export const onOfficeSalesSessionUpdated = (handler) => {
    const socket = getSocket()
    socket.on('officeSalesSession:updated', handler)
    return () => socket.off('officeSalesSession:updated', handler)
}

export const onOfficeSalesSessionUsers = (handler) => {
    const socket = getSocket()
    socket.on('officeSalesSession:users', handler)
    return () => socket.off('officeSalesSession:users', handler)
}

export const onOfficeSalesSessionCursor = (handler) => {
    const socket = getSocket()
    socket.on('officeSalesSession:cursor', handler)
    return () => socket.off('officeSalesSession:cursor', handler)
}

export const onOfficeSalesSessionAction = (handler) => {
    const socket = getSocket()
    socket.on('officeSalesSession:action', handler)
    return () => socket.off('officeSalesSession:action', handler)
}

export const onOfficeSalesSessionDenied = (handler) => {
    const socket = getSocket()
    socket.on('officeSalesSession:denied', handler)
    return () => socket.off('officeSalesSession:denied', handler)
}

export const onOfficeSalesSessionUserJoined = (handler) => {
    const socket = getSocket()
    socket.on('officeSalesSession:userJoined', handler)
    return () => socket.off('officeSalesSession:userJoined', handler)
}

export const onOfficeSalesSessionUserLeft = (handler) => {
    const socket = getSocket()
    socket.on('officeSalesSession:userLeft', handler)
    return () => socket.off('officeSalesSession:userLeft', handler)
}

export const onOfficeSalesSessionKicked = (handler) => {
    const socket = getSocket()
    socket.on('officeSalesSession:kicked', handler)
    return () => socket.off('officeSalesSession:kicked', handler)
}

export const onOfficeSalesSessionEnded = (handler) => {
    const socket = getSocket()
    socket.on('officeSalesSession:ended', handler)
    return () => socket.off('officeSalesSession:ended', handler)
}

export const emitOfficeSalesSessionSelection = ({
    officeId,
    sessionId,
    selection,
    user,
    role,
}, ack = noop) => {
    const socket = getSocket()

    socket.emit('officeSalesSession:selection', {
        officeId,
        sessionId: normalizeSessionId(sessionId),
        clientId: getSocketClientId(),
        selection: selection && typeof selection === 'object' ? selection : {},
        user,
        role,
    }, ack)
}

export const onOfficeSalesSessionSelection = (handler) => {
    const socket = getSocket()

    socket.on('officeSalesSession:selection', handler)

    return () => socket.off('officeSalesSession:selection', handler)
}

export const onOfficeSalesSessionSelectionSnapshot = (handler) => {
    const socket = getSocket()

    socket.on('officeSalesSession:selectionSnapshot', handler)

    return () => socket.off('officeSalesSession:selectionSnapshot', handler)
}

export const emitOfficeSalesSessionCamera = ({
    officeId,
    sessionId,
    camera,
    user,
    role,
}) => {
    const socket = getSocket()
    const payload = {
        officeId,
        sessionId: normalizeSessionId(sessionId),
        camera,
        user,
        role,
    }

    if (socket?.volatile && typeof socket.volatile.emit === 'function') {
        socket.volatile.emit('officeSalesSession:camera', payload)
        return
    }

    socket.emit('officeSalesSession:camera', payload)
}

export const onOfficeSalesSessionCamera = (handler) => {
    const socket = getSocket();

    socket.on('officeSalesSession:camera', handler);

    return () => socket.off('officeSalesSession:camera', handler);
};

export const emitOfficeSalesSessionWalk = ({
    officeId,
    sessionId,
    walk,
    user,
    role,
}) => {
    const socket = getSocket()
    const payload = {
        officeId,
        sessionId: normalizeSessionId(sessionId),
        walk: walk && typeof walk === 'object' ? walk : {},
        user,
        role,
    }

    if (socket?.volatile && typeof socket.volatile.emit === 'function') {
        socket.volatile.emit('officeSalesSession:walk', payload)
        return
    }

    socket.emit('officeSalesSession:walk', payload)
}

export const onOfficeSalesSessionWalk = (handler) => {
    const socket = getSocket()

    socket.on('officeSalesSession:walk', handler)

    return () => socket.off('officeSalesSession:walk', handler)
}


export const onSceneScreenshotChanged = (handler) => {
    const socket = getSocket()

    socket.on('sceneScreenshot:changed', handler)

    return () => socket.off('sceneScreenshot:changed', handler)
}
